export type NormalizedSubscriptionTier = 'free' | 'nobility' | 'royalty' | 'imperial';
export type ResolvedAccessStatus = 'active' | 'trialing' | 'inactive';
export type EffectiveSubscriptionStatus = ResolvedAccessStatus;

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const INACTIVE_SUBSCRIPTION_STATUSES = new Set([
  'canceled',
  'cancelled',
  'inactive',
  'pastDue',
  'incomplete',
  'incompleteExpired',
  'unpaid',
  'paused',
  'paymentFailed',
  'expired',
]);

function normalizeBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

export function normalizeSubscriptionTier(value: unknown): NormalizedSubscriptionTier {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'royal') return 'royalty';
  if (normalized === 'nobility' || normalized === 'royalty' || normalized === 'imperial') {
    return normalized;
  }
  return 'free';
}

export function normalizeSubscriptionStatus(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function resolveEffectiveSubscriptionAccess(input: {
  tier?: unknown;
  status?: unknown;
  cancelAtPeriodEnd?: unknown;
}): {
  tier: NormalizedSubscriptionTier;
  status: ResolvedAccessStatus;
  hasPaidAccess: boolean;
  cancelAtPeriodEnd: boolean;
  rawStatus: string;
} {
  const rawTier = normalizeSubscriptionTier(input.tier);
  const rawStatus = normalizeSubscriptionStatus(input.status);
  const cancelAtPeriodEnd = normalizeBoolean(input.cancelAtPeriodEnd);

  const isInactive =
    cancelAtPeriodEnd ||
    (rawStatus.length > 0 && INACTIVE_SUBSCRIPTION_STATUSES.has(rawStatus));

  const effectiveTier: NormalizedSubscriptionTier = isInactive ? 'free' : rawTier;

  let resolvedAccessStatus: ResolvedAccessStatus;
  if (isInactive) {
    resolvedAccessStatus = 'inactive';
  } else if (ACTIVE_SUBSCRIPTION_STATUSES.has(rawStatus)) {
    resolvedAccessStatus = rawStatus === 'trialing' ? 'trialing' : 'active';
  } else {
    resolvedAccessStatus = effectiveTier === 'free' ? 'inactive' : 'active';
  }

  return {
    tier: effectiveTier,
    status: resolvedAccessStatus,
    hasPaidAccess: effectiveTier !== 'free' && resolvedAccessStatus !== 'inactive',
    cancelAtPeriodEnd,
    rawStatus,
  };
}

export function hasPaidSubscriptionAccess(input: {
  tier?: unknown;
  status?: unknown;
  cancelAtPeriodEnd?: unknown;
}): boolean {
  return resolveEffectiveSubscriptionAccess(input).hasPaidAccess;
}
